import { useEffect, useState } from 'react'
import { Button } from '@heroui/react'
import { api } from '../../lib/api'
import { explain } from '../../lib/staffErrors'
import type { OperationProfileRow } from '../../lib/types'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { Notice, PageHead } from '../settingsKit'
import { EquipmentAddSheet, EquipmentList } from './EquipmentList'
import { EquipmentPage } from './EquipmentPage'

/** The address of the list, or of one equipment profile's page. */
export const openEquipment = (id: string) => {
  window.location.hash = id ? `equipment/${id}` : 'equipment'
}

/**
 * Settings → Equipment: the organisation's equipment profiles, in Basic and Advanced
 * alike, by operation profile. openId names one profile's page (#equipment/<id>).
 */
export function EquipmentSection({
  organisation,
  readOnly,
  openId,
}: {
  organisation?: string
  readOnly: boolean
  openId?: string
}) {
  const { lang, say } = useStaffText()
  const [profiles, setProfiles] = useState<OperationProfileRow[] | null>(null)
  const [chosen, setChosen] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    api
      .operationProfiles(organisation)
      .then((r) => {
        if (!alive) return
        setProfiles(r.profiles)
        setChosen((c) => (r.profiles.some((p) => p.id === c) ? c : (r.profiles[0]?.id ?? '')))
      })
      .catch((err) => alive && setError(explain(err, lang, 'common.unreachable')))
    return () => {
      alive = false
    }
  }, [organisation, lang])

  if (openId) {
    return (
      <EquipmentPage
        key={openId}
        id={openId}
        readOnly={readOnly}
        profiles={profiles ?? []}
        onBack={() => openEquipment('')}
        onProfile={setChosen}
        onOpen={openEquipment}
      />
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.equipment')}
        line={say('admin.hint.equipment')}
        actions={
          !readOnly && (
            <Button variant="primary" isDisabled={!chosen} onPress={() => setAdding(true)}>
              <Icon name="plus" size={16} /> {say('eq.tab.add')}
            </Button>
          )
        }
      />
      {error && <Notice>{error}</Notice>}
      {!profiles && !error && <p className="empty">{say('common.loading')}</p>}
      {chosen && (
        <EquipmentList
          operationProfileId={chosen}
          profiles={profiles && profiles.length > 1 ? profiles : undefined}
          onProfile={setChosen}
          onOpen={openEquipment}
        />
      )}
      {adding && chosen && (
        <EquipmentAddSheet
          operationProfileId={chosen}
          onClose={() => setAdding(false)}
          onMade={(id) => {
            setAdding(false)
            openEquipment(id)
          }}
        />
      )}
    </>
  )
}
